/**
 * Admin › Finanças › Ofertas de incentivo, the hourly earnings guarantee (W10-MISSIONS-GUARANTEE, D492, ADR-0108; lever L4) - the pure view-model behind the form where an
 * operator proposes a guarantee (OFF unless the market publishes `incentives.guaranteePolicy`), a DIFFERENT operator approves it (which reserves its budget in the DRIVER
 * envelope) and either may end it early. The generic approve / end of the offers screen already serve every lever; this holds only what is the guarantee's own:
 * the draft checks against the BOUNDS the governed policy publishes (the server re-checks every one) and the body of a proposal on whole UTC hours.
 * Nothing here names a country, a currency or an amount.
 */
import type { ActiveIncentivesListView, ProposeActiveIncentiveInput } from '@vello/api-client';

export type GuaranteePolicyView = NonNullable<ActiveIncentivesListView['guaranteePolicy']>;

export type GuaranteeDraftIssue =
  | 'RATE_REQUIRED' | 'RATE_OVER_POLICY' | 'MINUTES_UNDER_POLICY' | 'ACCEPTANCE_UNDER_POLICY' | 'BUDGET_REQUIRED' | 'REASON_REQUIRED' | 'START_REQUIRED' | 'HOURS_REQUIRED' | 'HOURS_OVER_POLICY';

export interface GuaranteeDraft {
  /** The guarantee per eligible hour, minor units; undefined until typed. */
  readonly rateMinor: bigint | undefined;
  readonly minOnlineMinutes: number;
  /** Basis points; 0 = no acceptance requirement. */
  readonly minAcceptanceBp: number;
  readonly budgetMinor: bigint | undefined;
  readonly reasonCode: string;
  /** Hours from the start of the next whole hour; undefined until chosen. */
  readonly startInHours: number | undefined;
  readonly durationHours: number | undefined;
}

export const EMPTY_GUARANTEE_DRAFT: GuaranteeDraft = { rateMinor: undefined, minOnlineMinutes: 45, minAcceptanceBp: 0, budgetMinor: undefined, reasonCode: '', startInHours: undefined, durationHours: undefined };

/** The starts an operator may choose (hours from the next whole hour) and the durations (hours), closed lists. */
export const GUARANTEE_START_CHOICES = [0, 1, 2, 3, 6, 12, 24] as const;
export const GUARANTEE_DURATION_CHOICES = [2, 4, 6, 8, 12, 24, 48, 72, 168, 336, 672] as const;

export function guaranteeDurations(maxHours: number): readonly number[] {
  return GUARANTEE_DURATION_CHOICES.filter((h) => h <= maxHours);
}

const minor = (m: { readonly amountMinor: string } | undefined): bigint | undefined => (m !== undefined && /^\d+$/.test(m.amountMinor) ? BigInt(m.amountMinor) : undefined);

/** The first thing wrong with a draft against the policy's published bounds, or undefined. */
export function guaranteeDraftIssue(d: GuaranteeDraft, policy: GuaranteePolicyView, reasons: readonly string[]): GuaranteeDraftIssue | undefined {
  if (d.rateMinor === undefined || d.rateMinor <= 0n) return 'RATE_REQUIRED';
  const max = minor(policy.maxGuaranteePerHour);
  if (max !== undefined && d.rateMinor > max) return 'RATE_OVER_POLICY';
  if (d.minOnlineMinutes < (policy.minOnlineMinutesFloor ?? 1) || d.minOnlineMinutes > 60) return 'MINUTES_UNDER_POLICY';
  if (d.minAcceptanceBp < (policy.minAcceptanceBpFloor ?? 0) || d.minAcceptanceBp > 10_000) return 'ACCEPTANCE_UNDER_POLICY';
  if (d.budgetMinor === undefined || d.budgetMinor <= 0n) return 'BUDGET_REQUIRED';
  if (!reasons.includes(d.reasonCode)) return 'REASON_REQUIRED';
  if (d.startInHours === undefined) return 'START_REQUIRED';
  if (d.durationHours === undefined) return 'HOURS_REQUIRED';
  if (d.durationHours > (policy.maxIncentiveHours ?? 0)) return 'HOURS_OVER_POLICY';
  return undefined;
}

/** The first whole UTC hour strictly after `nowIso`. */
export function nextWholeHour(nowIso: string): number {
  return (Math.floor(Date.parse(nowIso) / 3_600_000) + 1) * 3_600_000;
}

/** The body of a proposal: the window sits on whole UTC hours (the hours the closer counts); an actor or a currency is never part of it. */
export function guaranteeProposalBody(marketId: string, d: GuaranteeDraft, nowIso: string): ProposeActiveIncentiveInput {
  const from = nextWholeHour(nowIso) + (d.startInHours ?? 0) * 3_600_000;
  return {
    marketId,
    lever: 'EARNINGS_GUARANTEE',
    value: { guaranteeMinorPerHour: (d.rateMinor ?? 0n).toString(), minOnlineMinutes: d.minOnlineMinutes, minAcceptanceBp: d.minAcceptanceBp },
    spendCapMinor: (d.budgetMinor ?? 0n).toString(),
    reasonCode: d.reasonCode,
    validFrom: new Date(from).toISOString(),
    validTo: new Date(from + (d.durationHours ?? 0) * 3_600_000).toISOString(),
  };
}
